// A Discover package's element rules in words (keepr-api KPR-1710): the
// elements it keeps from some people, as the package page, the install review
// and the install's answer say them, and why one was (or would be) not
// applied. Pure: no API call. The update's elementRule changes are words the
// server writes (`summary`, `yours`, `theirs`).

/**
 * A package's element rules (keepr-api KPR-1710): the elements it keeps from
 * some people, as the package page, the install review and the answer say
 * them. Each entry is `{ card: { name }, element, label, column?, columnLabel?,
 * summary, addOn? }`; its element in words is the label, a column's as
 * "Group › Column".
 */
export function keptFieldLabel(rule) {
	if (!rule) return '';
	const label = rule.label || rule.element || '';
	return rule.column ? `${label} › ${rule.columnLabel || rule.column}` : label;
}

/**
 * The rules a package keeps, for the add-ons chosen (an add-on's rule only
 * when that add-on is chosen, or here already). `chosen` a Set of add-on
 * keys, or null for every rule.
 */
export function keptFieldsFor(rules, chosen = null) {
	const list = Array.isArray(rules) ? rules : [];
	return chosen ? list.filter(r => !r?.addOn || chosen.has(r.addOn)) : list;
}

/**
 * One sentence for the install review: how many elements the package keeps
 * from some people, switched on at install. '' for none.
 */
export function keptFieldsNote(count) {
	if (!count) return '';
	return count === 1
		? 'It keeps one element from some people, switched on as soon as it is added:'
		: `It keeps ${count} elements from some people, switched on as soon as it is added:`;
}

// Why a rule was (or would be) not applied, in a person's words.
const NOT_APPLIED_WORDS = {
	existing_rule: 'this collection already has its own rule for it, and that one is kept',
	element_grants_off: 'element access rules are not switched on on this server',
	element_rule_conflicts: 'something in this collection shows it, so the rule could not be saved',
	element_rule_widens: 'a collection under this one gives more people access to it',
	not_here: 'it is not on the card here',
	invalid: 'the package\'s rule could not be read',
};

/** One not-applied rule in words: "Medicaid number on Resident: this collection already has…". */
export function notAppliedWords(entry) {
	if (!entry) return '';
	const why = NOT_APPLIED_WORDS[entry.reason] || entry.message || 'it was not applied';
	const where = entry.card?.name ? ` on ${entry.card.name}` : '';
	return `${keptFieldLabel(entry)}${where}: ${why}.`;
}

/**
 * After an install or an add-on (keepr-api KPR-1710): what it could not keep
 * as the package says, in one sentence, or '' when every rule was applied.
 * With the server's switch off, one sentence for all of them.
 */
export function elementRulesAnswerNote(answer) {
	const off = answer && answer.enabled === false;
	const missed = Array.isArray(answer?.notApplied) ? answer.notApplied : [];
	if (!missed.length) return '';
	if (off) return `Element access rules are not switched on on this server, so ${missed.length === 1 ? 'the element it keeps from some people is' : `the ${missed.length} elements it keeps from some people are`} open to everyone the collection is shared with.`;
	if (missed.length === 1) return `One element is not kept as the package says. ${notAppliedWords(missed[0])}`;
	return `${missed.length} elements are not kept as the package says. ${missed.slice(0, 3).map(notAppliedWords).join(' ')}`;
}
